import { useState, useMemo, useEffect } from 'react';
import { usePhotos } from '../hooks/usePhotos';
import { useMusicTracks } from '../hooks/useMusicTracks';
import { useMusicPlayer } from '../context/MusicPlayerContext';
import { useGigs } from '../hooks/useGigs';
import { supabase } from '../lib/supabaseClient';
// useMusicPlayer -- global audio context so the MiniPlayer bar appears when playing
// useGigs -- needed to build the Gig Playlist from upcoming setlists

// ============================================================
// library.js - Contains TWO components in one file:
//   1. PhotoLibrary  - Upload, browse, filter, share, EDIT promo photos
//   2. MusicLibrary  - Upload, play, search, email music tracks
//
// ── CHANGES FROM PREVIOUS VERSION ────────────────────────────
//   PhotoLibrary:
//     • 'Waist Up' added as a fourth photo category alongside
//       Headshots, Full Body, and Performance.
//     • Edit photo details: clicking the pencil icon on any photo
//       card opens an Edit modal (same style as the Upload modal)
//       where the user can change the photo's name, category,
//       and tags. Calls updatePhoto() from usePhotos hook.
//       NOTE: usePhotos must export an updatePhoto(id, { name, category, tags })
//       function that does a Supabase UPDATE on the photos table.
//   MusicLibrary:
//     • No changes from previous version.
// ============================================================


// ============================================================
// PhotoLibrary - Browse and manage promo photos
// Features: upload, edit, categorize, tag, search, share, delete
// ============================================================
export function PhotoLibrary() {

  // ---- FILTER AND SEARCH STATE ----
  const [filter, setFilter] = useState('all')
  // 'all'         = show every category
  // 'headshots'   = headshots only
  // 'waist_up'    = waist-up shots only
  // 'full_body'   = full body shots only
  // 'performance' = performance shots only

  const [searchTerm, setSearchTerm] = useState('')
  // '' = no search filter active
  // any text = keep only photos whose name or tags include this term


  // ---- SELECTION STATE ----
  const [selectedPhotos, setSelectedPhotos] = useState([])
  // array of photo IDs that have their checkbox ticked
  // used for bulk share and delete operations


  // ---- UPLOAD MODAL STATE ----
  const [showUploadModal, setShowUploadModal] = useState(false)
  // true = the upload modal is open
  const [uploadInfo, setUploadInfo] = useState({
    file:     null,    // the File object from the file picker
    name:     '',      // display name for the photo
    category: 'headshots',  // which category to assign
    tags:     '',      // comma-separated tags string
    preview:  null     // object URL for the image preview thumbnail
  })
  const [uploading, setUploading] = useState(false)
  // true while Supabase storage + DB insert are running


  // ---- EDIT MODAL STATE ----
  const [showEditModal, setShowEditModal]   = useState(false)
  // true = the edit modal is open
  const [editingPhoto, setEditingPhoto]     = useState(null)
  // null = no photo being edited
  // photo object = the photo whose details are being changed
  const [editInfo, setEditInfo] = useState({
    name:     '',
    category: 'headshots',
    tags:     '',
  })
  // working copy of the fields being edited in the modal
  // does NOT update Supabase until the user clicks Save Changes
  const [editSaving, setEditSaving] = useState(false)
  // true while the Supabase UPDATE call is running


  // ---- PERFORMER NAME ----
  const [performerName, setPerformerName] = useState('')
  // loaded from user_metadata and used to build the share message
  //  "KingPerk_RedGownHeadshot"

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) setPerformerName(user.user_metadata?.performer_name || '')
    })
  }, [])


  // ---- HOOK - PHOTO DATA AND FUNCTIONS ----
  const { photos, loading, error, uploadPhoto, deletePhoto, updatePhoto } = usePhotos()
  // photos       = array of photo objects (each has .url attached)
  // uploadPhoto  = upload a new photo to Supabase Storage + insert DB row
  // deletePhoto  = delete photo from Storage + DB
  // updatePhoto  = UPDATE an existing photo row (name, category, tags)
  //                NOTE: add this to usePhotos.js if not already present:
  //                  async function updatePhoto(id, { name, category, tags }) {
  //                    const { error } = await supabase
  //                      .from('photos')
  //                      .update({ name, category, tags })
  //                      .eq('id', id)
  //                      .eq('user_id', user.id)
  //                    if (error) throw error
  //                    // also update local state to avoid a full refetch
  //                  }


  // ── CATEGORY DEFINITIONS ──────────────────────────────────────
  // Used for both the filter tabs and the category dropdowns in
  // the Upload and Edit modals.
  const categories = [
    { id: 'all',         label: 'All Photos'   },
    { id: 'headshots',   label: 'Headshots'    },
    { id: 'waist_up',    label: 'Waist Up'     },  // NEW
    { id: 'full_body',   label: 'Full Body'    },
    { id: 'performance', label: 'Performance'  },
  ]

  // Categories available in the upload/edit dropdowns (everything except 'all')
  const selectableCategories = categories.filter(c => c.id !== 'all')


  // -------------------------------------------------------
  // handleFileSelect - Fires when user picks a file to upload
  // -------------------------------------------------------
  const handleFileSelect = (e) => {
    const file = e.target.files[0]

    if (file) {
      setUploadInfo({
        file:     file,
        name:     file.name.replace(/\.[^/.]+$/, ''),
        // strip the file extension from the default name
        category: 'headshots',
        tags:     '',
        preview:  URL.createObjectURL(file)
        // createObjectURL = temporary local URL for preview without uploading
      })
      setShowUploadModal(true)
    }

    e.target.value = ''
    // reset file input so selecting the same file again triggers onChange
  };


  // -------------------------------------------------------
  // savePhoto - Uploads after the user fills in upload modal
  // -------------------------------------------------------
  const savePhoto = async () => {
    if (!uploadInfo.name || !uploadInfo.file) {
      alert('Please provide a name for the photo')
      return
    }

    try {
      setUploading(true)

      const tagsArray = uploadInfo.tags
        .split(',')
        .map(tag => tag.trim().toLowerCase())
        .filter(tag => tag !== '')
      // "red, Glamour, 2024" becomes ["red", "glamour", "2024"]

      await uploadPhoto(uploadInfo.file, {
        name:     uploadInfo.name,
        category: uploadInfo.category,
        tags:     tagsArray
      })

      setShowUploadModal(false)
      setUploadInfo({ file: null, name: '', category: 'headshots', tags: '', preview: null })
      alert('Photo uploaded successfully!')

    } catch (err) {
      alert('Error uploading photo: ' + err.message)
    } finally {
      setUploading(false)
    }
  };


  // -------------------------------------------------------
  // openEditModal (NEW)
  // Pre-fills the edit modal with the photo's current data
  // and opens it. Called by the pencil button on each card.
  // -------------------------------------------------------
  const openEditModal = (photo) => {
    // photo = the full photo object from the photos array

    setEditingPhoto(photo)
    // remember which photo we're editing

    setEditInfo({
      name:     photo.name     || '',
      category: photo.category || 'headshots',
      tags:     (photo.tags || []).join(', '),
      // join the stored tags array back into a comma-separated string
      // for the text input: ["red", "glamour"] -> "red, glamour"
    })

    setShowEditModal(true)
    // show the edit modal
  };


  // -------------------------------------------------------
  // savePhotoEdit (NEW)
  // Persists the changes from the edit modal to Supabase.
  // -------------------------------------------------------
  const savePhotoEdit = async () => {
    if (!editInfo.name.trim()) {
      alert('Photo name cannot be empty.')
      return
    }

    try {
      setEditSaving(true)

      const tagsArray = editInfo.tags
        .split(',')
        .map(tag => tag.trim().toLowerCase())
        .filter(tag => tag !== '')
      // same tag cleaning as the upload flow

      await updatePhoto(editingPhoto.id, {
        name:     editInfo.name.trim(),
        category: editInfo.category,
        tags:     tagsArray,
      })
      // updatePhoto calls Supabase UPDATE and updates local state in the hook

      setShowEditModal(false)
      setEditingPhoto(null)
      setEditInfo({ name: '', category: 'headshots', tags: '' })

    } catch (err) {
      alert('Error saving photo details: ' + err.message)
      console.error('Edit photo error:', err)
    } finally {
      setEditSaving(false)
    }
  };


  // -------------------------------------------------------
  // cancelEditModal (NEW)
  // Closes the edit modal without saving.
  // -------------------------------------------------------
  const cancelEditModal = () => {
    setShowEditModal(false)
    setEditingPhoto(null)
    setEditInfo({ name: '', category: 'headshots', tags: '' })
  };


  // -------------------------------------------------------
  // toggleSelect - Adds or removes a photo from the selection
  // -------------------------------------------------------
  const toggleSelect = (photoId) => {
    if (selectedPhotos.includes(photoId)) {
      setSelectedPhotos(selectedPhotos.filter(id => id !== photoId))
      // deselect: remove this ID from the array
    } else {
      setSelectedPhotos([...selectedPhotos, photoId])
      // select: append this ID to the array
    }
  };


  // -------------------------------------------------------
  // selectAll - Selects or deselects all visible photos
  // -------------------------------------------------------
  const selectAll = () => {
    const filtered = filteredPhotos()

    if (selectedPhotos.length === filtered.length) {
      setSelectedPhotos([])
    } else {
      setSelectedPhotos(filtered.map(p => p.id))
    }
  };


  // -------------------------------------------------------
  // handleDeleteSelected - Deletes all selected photos
  // -------------------------------------------------------
  const handleDeleteSelected = async () => {
    if (!window.confirm(`Delete ${selectedPhotos.length} photo(s)?`)) return

    try {
      const photosToDelete = photos.filter(p => selectedPhotos.includes(p.id))

      await Promise.all(
        photosToDelete.map(photo => deletePhoto(photo.id, photo.file_path))
        // Promise.all = run all deletes simultaneously
      )

      setSelectedPhotos([])
      alert('Photos deleted successfully!')

    } catch (err) {
      alert('Error deleting photos: ' + err.message)
    }
  };


  // -------------------------------------------------------
  // sharePhotos - Copies a formatted message to the clipboard
  // -------------------------------------------------------
  const sharePhotos = async () => {
    const selected = photos.filter(p => selectedPhotos.includes(p.id))

    const cleanPerformer = (performerName || 'Performer')
      .trim()
      .replace(/[^a-zA-Z0-9\s_]/g, '')
      .replace(/\s+/g, '_')

    const photoLines = selected.map(photo => {
      const { data } = supabase.storage
        .from('photos')
        .getPublicUrl(photo.file_path)
      const cleanPhotoName = photo.name
        .trim()
        .replace(/[^a-zA-Z0-9\s_]/g, '')
        .replace(/\s+/g, '_')
      const label = `${cleanPerformer}_${cleanPhotoName}`
      return `${label}\n(${data.publicUrl})`
    }).join('\n\n')

    const count    = selected.length
    const greeting = `Hi! Here ${count === 1 ? 'is' : 'are'} my promo photo${count === 1 ? '' : 's'} for the show.`

    const message =
`${greeting}

To save: click each link below to view the full quality photo in your browser. Once it is open, right-click the image (or long-press on mobile) and select "Save image as" to download it to your device. Please save directly from the link rather than screenshotting so the image quality is preserved for any promotional use.

${photoLines}

Thanks!`

    try {
      await navigator.clipboard.writeText(message)
      alert('Message copied to clipboard! Paste it into a DM or email to share your photos.')
    } catch {
      alert('Copy this message:\n\n' + message)
    }
  };


  // -------------------------------------------------------
  // filteredPhotos - Photos matching current category filter AND search
  // -------------------------------------------------------
  const filteredPhotos = () => {
    let result = photos

    if (filter !== 'all') {
      result = result.filter(p => p.category === filter)
      // keep only photos in the selected category
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase()
      result = result.filter(p =>
        p.name.toLowerCase().includes(term)
        ||
        (p.tags && p.tags.some(tag => tag.includes(term)))
        // match on name OR any single tag
      )
    }

    return result
  };


  // -------------------------------------------------------
  // allTags - Unique sorted list of every tag across all photos
  // -------------------------------------------------------
  const allTags = () => {
    const tags = photos.flatMap(p => p.tags || [])
    // flatMap = flatten the array of tag arrays into one flat array
    return [...new Set(tags)].sort()
    // Set removes duplicates, sort = alphabetical
  };


  if (loading) return <div className="library-page"><h1 className="page-title">Promo Shots</h1><p>Loading...</p></div>
  if (error)   return <div className="library-page"><h1 className="page-title">Promo Shots</h1><p style={{ color: 'var(--status-error)' }}>Error: {error}</p></div>

  return (
    <div className="library-page">

      {/* ── PAGE HEADER ──────────────────────────────────────── */}
      <div className="page-header-row">
        <h1 className="page-title">Promo Shots</h1>

        {/* Hidden file input triggered by clicking the label */}
        <label className="btn-primary upload-btn">
          <i className="bi bi-upload"></i> Upload
          <input
            type="file"
            accept="image/*"
            // accept="image/*" = show only image files in the file picker
            onChange={handleFileSelect}
            hidden
          />
        </label>
      </div>

      {/* ── SEARCH BAR ───────────────────────────────────────── */}
      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by name or tag..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button className="clear-search" onClick={() => setSearchTerm('')}>
            <i className="bi bi-x"></i>
          </button>
        )}
      </div>

      {/* ── CATEGORY FILTER TABS ─────────────────────────────── */}
      {/* 'Waist Up' now appears between Headshots and Full Body */}
      <div className="filter-tabs">
        {categories.map(cat => (
          <button
            key={cat.id}
            className={filter === cat.id ? 'active' : ''}
            // 'active' class = highlighted/selected tab style
            onClick={() => setFilter(cat.id)}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* ── TAG FILTER BUTTONS ───────────────────────────────── */}
      {allTags().length > 0 && (
        <div className="tag-filters">
          {allTags().map(tag => (
            <button
              key={tag}
              className={`tag-btn ${searchTerm === tag ? 'active' : ''}`}
              onClick={() => setSearchTerm(searchTerm === tag ? '' : tag)}
              // clicking an active tag clears the search; clicking inactive sets it
            >
              #{tag}
            </button>
          ))}
        </div>
      )}

      {/* ── SELECTION BAR ────────────────────────────────────── */}
      {photos.length > 0 && (
        <div className="selection-bar">
          <button onClick={selectAll} className="btn-secondary">
            {selectedPhotos.length === filteredPhotos().length && filteredPhotos().length > 0
              ? 'Deselect All'
              : 'Select All'}
          </button>
          {selectedPhotos.length > 0 && (
            <>
              <button onClick={sharePhotos} className="btn-primary">
                <i className="bi bi-clipboard"></i> Copy Share Message
              </button>
              <button onClick={handleDeleteSelected} className="btn-danger">
                <i className="bi bi-trash"></i> Delete
              </button>
            </>
          )}
        </div>
      )}

      {/* Share tip */}
      {selectedPhotos.length > 0 && (
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--neutral-1)', opacity: 0.6, margin: '-0.25rem 0 0.75rem', paddingLeft: '0.25rem' }}>
          <i className="bi bi-info-circle" style={{ marginRight: '0.3rem' }}></i>
          Copies a message with your photo name{selectedPhotos.length > 1 ? 's' : ''} and download link{selectedPhotos.length > 1 ? 's' : ''} paste it into a DM or email.
        </p>
      )}

      {/* ── PHOTO GRID ───────────────────────────────────────── */}
      <div className="photo-grid">
        {photos.length === 0 ? (
          <p>No photos yet. Upload your first promo shot!</p>
        ) : filteredPhotos().length === 0 ? (
          <p>No photos match your search.</p>
        ) : (
          filteredPhotos().map(photo => (
            <div
              key={photo.id}
              className={`photo-item ${selectedPhotos.includes(photo.id) ? 'selected' : ''}`}
            >
              {/* Checkbox overlay -- top-left corner of the card */}
              <div className="photo-overlay">
                <input
                  type="checkbox"
                  className="photo-checkbox"
                  checked={selectedPhotos.includes(photo.id)}
                  onChange={() => toggleSelect(photo.id)}
                  onClick={(e) => e.stopPropagation()}
                  // stopPropagation prevents the img click from also firing
                />
              </div>

              {/* ── EDIT BUTTON (NEW) ── top-right corner ──────── */}
              {/* Positioned absolutely so it overlays the image */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  // prevent click from toggling the photo's selection checkbox
                  openEditModal(photo)
                }}
                title="Edit photo details"
                style={{
                  position:   'absolute',
                  top:        '0.4rem',
                  right:      '0.4rem',
                  // absolute position relative to the .photo-item (needs position: relative in CSS)
                  zIndex:     2,
                  background: 'rgba(0,0,0,0.55)',
                  // semi-transparent dark background so the icon is legible on any photo
                  border:     'none',
                  borderRadius: '50%',
                  width:      '28px',
                  height:     '28px',
                  display:    'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color:      'white',
                  cursor:     'pointer',
                  fontSize:   '0.75rem',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--brand-primary)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.55)'}
              >
                <i className="bi bi-pencil-fill"></i>
              </button>

              <img
                src={photo.url}
                alt={photo.name}
                onError={(e) => {
                  console.error('Image failed to load:', photo.url)
                  e.target.src = 'data:image/svg+xml,...'
                }}
                onClick={() => toggleSelect(photo.id)}
                style={{ cursor: 'pointer' }}
              />

              <div className="photo-info">
                <p className="photo-name">{photo.name}</p>

                {/* Category badge (NEW) -- small label under the name */}
                {photo.category && (
                  <p style={{ fontSize: '0.7rem', color: 'var(--neutral-1)', opacity: 0.55, margin: '0 0 0.25rem' }}>
                    {
                      // Map the stored category key to a human-readable label
                      categories.find(c => c.id === photo.category)?.label || photo.category
                    }
                  </p>
                )}

                {photo.tags && photo.tags.length > 0 && (
                  <div className="photo-tags">
                    {photo.tags.map(tag => (
                      <span key={tag} className="tag">#{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>


      {/* ── UPLOAD MODAL ─────────────────────────────────────── */}
      {showUploadModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Add Photo <i className="bi bi-camera-fill"></i></h2>

            {uploadInfo.preview && (
              <div className="upload-preview">
                <img src={uploadInfo.preview} alt="Preview" />
              </div>
            )}

            <div className="form-group">
              <label>Photo Name</label>
              <input
                type="text"
                placeholder=" Red Gown Headshot"
                value={uploadInfo.name}
                onChange={(e) => setUploadInfo({...uploadInfo, name: e.target.value})}
              />
            </div>

            <div className="form-group">
              <label>Category</label>
              <select
                value={uploadInfo.category}
                onChange={(e) => setUploadInfo({...uploadInfo, category: e.target.value})}
              >
                {selectableCategories.map(cat => (
                  // render one option per selectable category (excludes 'all')
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Tags (comma separated)</label>
              <input
                type="text"
                placeholder=" red, glamour, 2024, drag brunch"
                value={uploadInfo.tags}
                onChange={(e) => setUploadInfo({...uploadInfo, tags: e.target.value})}
              />
              <small>Add tags to find this photo later</small>
            </div>

            <div className="modal-buttons">
              <button className="btn-primary" onClick={savePhoto} disabled={!uploadInfo.name || uploading}>
                {uploading ? 'Uploading...' : 'Save Photo'}
              </button>
              <button className="btn-secondary" onClick={() => setShowUploadModal(false)} disabled={uploading}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}


      {/* ── EDIT PHOTO MODAL (NEW) ─────────────────────────────
          Opens when the pencil button on a photo card is clicked.
          Lets the user rename the photo, change its category,
          or update its tags. Does NOT change the actual image file.
          ──────────────────────────────────────────────────────── */}
      {showEditModal && editingPhoto && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Edit Photo Details <i className="bi bi-pencil-fill"></i></h2>

            {/* Small preview of the photo being edited */}
            <div className="upload-preview" style={{ marginBottom: '1rem' }}>
              <img src={editingPhoto.url} alt={editingPhoto.name} />
            </div>

            {/* ── PHOTO NAME ──────────────────────────────────── */}
            <div className="form-group">
              <label>Photo Name</label>
              <input
                type="text"
                placeholder=" Red Gown Headshot"
                value={editInfo.name}
                onChange={(e) => setEditInfo({ ...editInfo, name: e.target.value })}
                // spread existing editInfo, override just the name
                disabled={editSaving}
                autoFocus
                // autoFocus = keyboard jumps to name field when modal opens
              />
            </div>

            {/* ── CATEGORY ────────────────────────────────────── */}
            <div className="form-group">
              <label>Category</label>
              <select
                value={editInfo.category}
                onChange={(e) => setEditInfo({ ...editInfo, category: e.target.value })}
                disabled={editSaving}
              >
                {selectableCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                  // one option per selectable category (excludes 'all')
                ))}
              </select>
            </div>

            {/* ── TAGS ────────────────────────────────────────── */}
            <div className="form-group">
              <label>Tags (comma separated)</label>
              <input
                type="text"
                placeholder=" red, glamour, 2024, drag brunch"
                value={editInfo.tags}
                onChange={(e) => setEditInfo({ ...editInfo, tags: e.target.value })}
                disabled={editSaving}
              />
              <small>Separate multiple tags with commas</small>
            </div>

            {/* ── MODAL BUTTONS ───────────────────────────────── */}
            <div className="modal-buttons">
              <button
                className="btn-primary"
                onClick={savePhotoEdit}
                disabled={editSaving || !editInfo.name.trim()}
                // disabled while saving OR if name has been cleared
              >
                {editSaving ? 'Saving...' : <><i className="bi bi-floppy me-1"></i>Save Changes</>}
              </button>
              <button
                className="btn-secondary"
                onClick={cancelEditModal}
                disabled={editSaving}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}


// ============================================================
// MusicLibrary - Browse and manage music tracks
// No changes from previous version.
// ============================================================
export function MusicLibrary() {

  // ---- GLOBAL AUDIO PLAYER ----
  const {
    currentTrack: globalCurrentTrack,
    isPlaying,
    playTrack:    playGlobalTrack,
    togglePlay:   globalTogglePlay,
    stop:         stopGlobal,
    setQueue,
  } = useMusicPlayer()

  // ---- GIG DATA ----
  const { gigs } = useGigs()

  // ---- GIG PLAYLIST STATE ----
  const [gigPlaylistRange, setGigPlaylistRange] = useState(null)

  // ---- SELECTION STATE ----
  const [selectedTracks, setSelectedTracks] = useState([])

  // ---- MODAL STATE ----
  const [showShareModal, setShowShareModal]   = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)

  // ---- SEARCH STATE ----
  const [searchTerm, setSearchTerm] = useState('')

  // ---- UPLOAD STATE ----
  const [uploading, setUploading] = useState(false)
  const [uploadInfo, setUploadInfo] = useState({ file: null, tags: '', name: '' })

  // ---- SHARE MODAL STATE ----
  const [shareInfo, setShareInfo] = useState({ performerName: '', showName: '' })

  // ---- HOOK ----
  const { tracks, loading, error, uploadTrack, deleteTrack, updateTrack } = useMusicTracks()

  // ---- INLINE EDIT STATE ----
  const [editingTrack, setEditingTrack] = useState(null)
  const [editName, setEditName]         = useState('')
  const [editTags, setEditTags]         = useState('')
  const [saving, setSaving]             = useState(false)

  const openEdit = (track) => {
    setEditingTrack(track.id)
    setEditName(track.name)
    setEditTags((track.tags || []).join(', '))
  }

  const cancelEdit = () => {
    setEditingTrack(null)
    setEditName('')
    setEditTags('')
  }

  const saveEdit = async (trackId) => {
    if (!editName.trim()) { alert('Track name cannot be empty.'); return }
    setSaving(true)
    try {
      const tagsArray = editTags.split(',').map(t => t.trim().toLowerCase()).filter(t => t !== '')
      await updateTrack(trackId, { name: editName.trim(), tags: tagsArray })
      cancelEdit()
    } catch (err) {
      alert('Error saving track: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const buildDownloadUrl = (track) => {
    const baseUrl  = (track.url || '').split('?')[0]
    const ext      = baseUrl.split('.').pop() || 'mp3'
    const cleanName = (track.name || 'track').trim().replace(/[^a-zA-Z0-9\s\-_]/g, '').replace(/\s+/g, '_') || 'track'
    return `${baseUrl}?download=${encodeURIComponent(cleanName)}.${ext}`
  }

  const gigPlaylistTracks = useMemo(() => {
    if (!gigPlaylistRange) return []

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const endDate = new Date(today)
    if (gigPlaylistRange === 'week')   endDate.setDate(today.getDate() + 7)
    if (gigPlaylistRange === '2weeks') endDate.setDate(today.getDate() + 14)
    if (gigPlaylistRange === 'month')  endDate.setDate(today.getDate() + 31)

    const upcomingGigs = gigs
      .filter(gig => {
        const parts   = gig.date.split('T')[0].split('-')
        const gigDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]))
        gigDate.setHours(0, 0, 0, 0)
        return gigDate >= today && gigDate <= endDate
      })
      .sort((a, b) => new Date(a.date.split('T')[0]) - new Date(b.date.split('T')[0]))

    const seen = new Set()
    const trackIds = []
    for (const gig of upcomingGigs) {
      for (const id of (gig.setlist || [])) {
        if (!seen.has(id)) { seen.add(id); trackIds.push(id) }
      }
    }

    return trackIds.map(id => tracks.find(t => t.id === id)).filter(Boolean)
  }, [gigPlaylistRange, gigs, tracks])

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      setUploadInfo({ file, name: file.name.replace(/\.[^/.]+$/, ''), tags: '' })
      setShowUploadModal(true)
    }
    e.target.value = ''
  };

  const saveTrack = async () => {
    if (!uploadInfo.name || !uploadInfo.file) { alert('Please provide a name for the track'); return }
    try {
      setUploading(true)
      const tagsArray = uploadInfo.tags.split(',').map(tag => tag.trim().toLowerCase()).filter(tag => tag !== '')
      await uploadTrack(uploadInfo.file, { name: uploadInfo.name, tags: tagsArray })
      setShowUploadModal(false)
      setUploadInfo({ file: null, tags: '', name: '' })
      alert('Track uploaded successfully!')
    } catch (err) {
      alert('Error uploading track: ' + err.message)
    } finally {
      setUploading(false)
    }
  };

  const togglePlay = (track) => {
    const isThisTrackPlaying = globalCurrentTrack?.url === track.url && isPlaying
    if (isThisTrackPlaying) {
      globalTogglePlay()
    } else {
      const visibleTracks = filteredTracks().map(t => ({ name: t.name, url: t.url }))
      const startIndex = visibleTracks.findIndex(t => t.url === track.url)
      if (typeof setQueue === 'function') {
        setQueue(visibleTracks, startIndex >= 0 ? startIndex : 0)
      } else {
        playGlobalTrack({ name: track.name, url: track.url })
      }
    }
  };

  const toggleSelect = (trackId) => {
    if (selectedTracks.includes(trackId)) {
      setSelectedTracks(selectedTracks.filter(id => id !== trackId))
    } else {
      setSelectedTracks([...selectedTracks, trackId])
    }
  };

  const selectAll = () => {
    const filtered = filteredTracks()
    if (selectedTracks.length === filtered.length && filtered.length > 0) {
      setSelectedTracks([])
    } else {
      setSelectedTracks(filtered.map(t => t.id))
    }
  };

  const handleDeleteSelected = async () => {
    if (!window.confirm(`Delete ${selectedTracks.length} track(s)?`)) return
    try {
      const tracksToDelete = tracks.filter(t => selectedTracks.includes(t.id))
      await Promise.all(tracksToDelete.map(track => deleteTrack(track.id, track.file_path)))
      setSelectedTracks([])
      if (typeof stopGlobal === 'function') stopGlobal()
      alert('Tracks deleted successfully!')
    } catch (err) {
      alert('Error deleting tracks: ' + err.message)
    }
  };

  const sendTracks = async () => {
    const selected = selectedTracks.map(id => tracks.find(t => t.id === id)).filter(Boolean)
    const count    = selected.length
    const songWord = count === 1 ? 'song' : 'songs'
    const performerLabel = shareInfo.performerName || 'Your Performer'
    const showLabel      = shareInfo.showName      || 'the show'

    const title = `${performerLabel}'s ${songWord} for ${showLabel}: ${count} track${count !== 1 ? 's' : ''}`

    const trackLines = selected.map((t, index) => {
      const trackNumber   = index + 1
      const cleanPerformer = (shareInfo.performerName || 'Performer').trim().replace(/[^a-zA-Z0-9\s\-_]/g, '').replace(/\s+/g, '_')
      const cleanTrackName = t.name.trim().replace(/[^a-zA-Z0-9\s\-_]/g, '').replace(/\s+/g, '_')
      const ext            = t.url.split('?')[0].split('.').pop() || 'mp3'
      const displayName    = `${cleanPerformer}_${trackNumber}_${cleanTrackName}.${ext}`
      const baseUrl        = t.url.split('?')[0]
      const downloadUrl    = `${baseUrl}?download=${encodeURIComponent(displayName)}`
      return `• ${displayName}\n  ${downloadUrl}`
    }).join('\n\n')

    const bodyText =
`Hi!

${performerLabel} here. Here ${count === 1 ? 'is my' : 'are my'} ${songWord} for ${showLabel}:

${trackLines}

Thanks so much!
${performerLabel}`

    window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(bodyText)}`
    setShowShareModal(false)
  };

  const filteredTracks = () => {
    if (!searchTerm.trim()) return tracks
    const term = searchTerm.toLowerCase()
    return tracks.filter(track =>
      track.name.toLowerCase().includes(term)
      || (track.tags && track.tags.some(tag => tag.includes(term)))
    )
  };

  const allTags = () => {
    const tags = tracks.flatMap(t => t.tags || [])
    return [...new Set(tags)].sort()
  };

  if (loading) return <div className="library-page"><h1 className="page-title">Music Library</h1><p>Loading...</p></div>
  if (error)   return <div className="library-page"><h1 className="page-title">Music Library</h1><p style={{ color: 'var(--status-error)' }}>Error: {error}</p></div>

  return (
    <div className="library-page">
      <div className="page-header-row">
        <h1 className="page-title">Music Library</h1>
        <label className="btn-primary upload-btn">
          <i className="bi bi-upload"></i> Upload
          <input type="file" accept="audio/*" onChange={handleFileSelect} hidden />
        </label>
      </div>

      {/* GIG PLAYLIST */}
      <div style={{ background: 'var(--brand-white)', border: '2px solid var(--accent-color)', borderRadius: 'var(--radius-card)', padding: 'var(--spacing-md)', marginBottom: 'var(--spacing-lg)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div>
            <p style={{ fontWeight: 'var(--weight-black)', color: 'var(--neutral-1)', margin: 0, fontSize: 'var(--text-base)' }}>
              <i className="bi bi-calendar-music" style={{ color: 'var(--primary)', marginRight: '0.4rem' }}></i>
              Gig Playlist
            </p>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--neutral-1)', opacity: 0.6, margin: '0.15rem 0 0' }}>
              Build a queue from your upcoming setlists
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            {[{ key: 'week', label: 'This Week' }, { key: '2weeks', label: 'Next 2 Weeks' }, { key: 'month', label: 'Next 30 Days' }].map(opt => (
              <button key={opt.key} onClick={() => setGigPlaylistRange(gigPlaylistRange === opt.key ? null : opt.key)}
                style={{ padding: '0.35rem 0.85rem', borderRadius: 'var(--radius-full)', border: `2px solid ${gigPlaylistRange === opt.key ? 'var(--accent-color)' : 'var(--primary)'}`, background: gigPlaylistRange === opt.key ? 'var(--primary)' : 'transparent', color: gigPlaylistRange === opt.key ? 'white' : 'var(--primary-accessible)', fontWeight: 'var(--weight-bold)', fontSize: 'var(--text-sm)', cursor: 'pointer', fontFamily: 'var(--font-body)', transition: 'var(--transition-all)' }}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {gigPlaylistRange && (
          <div style={{ marginTop: 'var(--spacing-md)', borderTop: '1px solid var(--accent-color)', paddingTop: 'var(--spacing-md)' }}>
            {gigPlaylistTracks.length === 0 ? (
              <p style={{ color: 'var(--neutral-1)', opacity: 0.6, fontSize: 'var(--text-sm)', margin: 0 }}>
                No setlist tracks found for upcoming gigs in this window. Pin songs to your gigs on the Gig detail page.
              </p>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--spacing-sm)', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--neutral-1)', opacity: 0.75 }}>
                    {gigPlaylistTracks.length} track{gigPlaylistTracks.length !== 1 ? 's' : ''} across your upcoming gigs
                  </p>
                  <button className="btn-primary" style={{ padding: '0.4rem 1.1rem', fontSize: 'var(--text-sm)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
                    onClick={() => { const queue = gigPlaylistTracks.map(t => ({ name: t.name, url: t.url })); typeof setQueue === 'function' ? setQueue(queue, 0) : playGlobalTrack(queue[0]) }}>
                    <i className="bi bi-play-fill"></i> Play All
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: '260px', overflowY: 'auto' }}>
                  {gigPlaylistTracks.map((track, index) => {
                    const isThisTrackPlaying = globalCurrentTrack?.url === track.url && isPlaying
                    return (
                      <div key={track.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.75rem', background: isThisTrackPlaying ? 'var(--pink-pale)' : 'var(--neutral-bg)', border: `1.5px solid ${isThisTrackPlaying ? 'var(--primary)' : 'var(--accent-color)'}`, borderRadius: 'var(--radius-sm)', transition: 'var(--transition-all)' }}>
                        <span style={{ color: 'var(--neutral-1)', opacity: 0.4, minWidth: '1.5rem', fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-bold)', textAlign: 'right' }}>{index + 1}</span>
                        <button onClick={() => { if (isThisTrackPlaying) { globalTogglePlay() } else { const queue = gigPlaylistTracks.map(t => ({ name: t.name, url: t.url })); typeof setQueue === 'function' ? setQueue(queue, index) : playGlobalTrack({ name: track.name, url: track.url }) } }}
                          style={{ width: '30px', height: '30px', borderRadius: '50%', background: 'var(--primary)', color: 'white', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, fontSize: '0.85rem' }}>
                          <i className={`bi ${isThisTrackPlaying ? 'bi-pause-fill' : 'bi-play-fill'}`}></i>
                        </button>
                        <span style={{ flex: 1, minWidth: 0, fontWeight: 'var(--weight-bold)', color: isThisTrackPlaying ? 'var(--primary-accessible)' : 'var(--neutral-1)', fontSize: 'var(--text-sm)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{track.name}</span>
                        {track.tags && track.tags.length > 0 && <span style={{ fontSize: '0.7rem', color: 'var(--primary-accessible)', opacity: 0.75, flexShrink: 0 }}>#{track.tags[0]}</span>}
                      </div>
                    )
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Search */}
      <div className="search-bar">
        <input type="text" placeholder="Search by name or tag..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        {searchTerm && <button className="clear-search" onClick={() => setSearchTerm('')}><i className="bi bi-x"></i></button>}
      </div>

      {/* Tag filters */}
      {allTags().length > 0 && (
        <div className="tag-filters">
          {allTags().map(tag => (
            <button key={tag} className={`tag-btn ${searchTerm === tag ? 'active' : ''}`} onClick={() => setSearchTerm(searchTerm === tag ? '' : tag)}>#{tag}</button>
          ))}
        </div>
      )}

      {/* Selection bar */}
      {tracks.length > 0 && (
        <div className="selection-bar">
          <button onClick={selectAll} className="btn-secondary">
            {selectedTracks.length === filteredTracks().length && filteredTracks().length > 0 ? 'Deselect All' : 'Select All'}
          </button>
          {selectedTracks.length > 0 && (
            <>
              <button onClick={() => setShowShareModal(true)} className="btn-primary">
                <i className="bi bi-envelope"></i> Send {selectedTracks.length} Track{selectedTracks.length > 1 ? 's' : ''}
              </button>
              <button onClick={handleDeleteSelected} className="btn-danger"><i className="bi bi-trash"></i> Delete</button>
            </>
          )}
        </div>
      )}

      {/* Track list */}
      <div className="music-list">
        {tracks.length === 0 ? (
          <p>No tracks yet. Upload your first mix!</p>
        ) : filteredTracks().length === 0 ? (
          <p>No tracks match "{searchTerm}"</p>
        ) : (
          filteredTracks().map(track => (
            <div key={track.id} className={`music-track ${selectedTracks.includes(track.id) ? 'selected' : ''}`}>
              <input type="checkbox" checked={selectedTracks.includes(track.id)} onChange={() => toggleSelect(track.id)} />
              <button onClick={() => togglePlay(track)} className="play-btn">
                {globalCurrentTrack?.url === track.url && isPlaying ? <i className="bi bi-pause-fill"></i> : <i className="bi bi-play-fill"></i>}
              </button>
              <a href={buildDownloadUrl(track)} download title={`Download ${track.name}`} onClick={(e) => e.stopPropagation()}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: 'var(--radius-full)', background: 'rgba(101,2,94,0.06)', border: '1.5px solid var(--accent-color)', color: 'var(--primary-accessible)', fontSize: '0.9rem', flexShrink: 0, textDecoration: 'none', transition: 'var(--transition-all)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--pink-pale)'; e.currentTarget.style.borderColor = 'var(--primary)' }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(101,2,94,0.06)'; e.currentTarget.style.borderColor = 'var(--accent-color)' }}>
                <i className="bi bi-download"></i>
              </a>
              <div className="track-info" style={{ flex: 1, minWidth: 0 }}>
                {editingTrack === track.id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(track.id); if (e.key === 'Escape') cancelEdit() }} placeholder="Track name" autoFocus
                      style={{ padding: '0.3rem 0.5rem', border: '2px solid var(--primary)', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-bold)', color: 'var(--neutral-1)', width: '100%' }} />
                    <input type="text" value={editTags} onChange={(e) => setEditTags(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(track.id); if (e.key === 'Escape') cancelEdit() }} placeholder="Tags: beyonce, pop, upbeat"
                      style={{ padding: '0.3rem 0.5rem', border: '2px solid var(--accent-color)', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: '0.75rem', color: 'var(--neutral-1)', width: '100%' }} />
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button className="btn-primary" style={{ padding: '0.25rem 0.75rem', fontSize: 'var(--text-sm)' }} onClick={() => saveEdit(track.id)} disabled={saving}>
                        {saving ? 'Saving...' : <><i className="bi bi-check-lg"></i> Save</>}
                      </button>
                      <button className="btn-secondary" style={{ padding: '0.25rem 0.6rem', fontSize: 'var(--text-sm)' }} onClick={cancelEdit} disabled={saving}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <span className="track-name">{track.name}</span>
                      <button onClick={() => openEdit(track)} title="Edit name and tags" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary-accessible)', padding: '0.1rem 0.2rem', fontSize: '0.8rem', opacity: 0.7, flexShrink: 0 }}>
                        <i className="bi bi-pencil-fill"></i>
                      </button>
                    </div>
                    {track.tags && track.tags.length > 0 && (
                      <div className="track-tags">
                        {track.tags.map(tag => <span key={tag} className="tag" onClick={() => setSearchTerm(tag)}>#{tag}</span>)}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Upload modal */}
      {showUploadModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Add Track <i className="bi bi-music-note-beamed"></i></h2>
            <div className="form-group">
              <label>Track Name</label>
              <input type="text" placeholder=" Beyoncé Megamix" value={uploadInfo.name} onChange={(e) => setUploadInfo({...uploadInfo, name: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Tags (comma separated)</label>
              <input type="text" placeholder=" beyonce, brunch, upbeat, pop" value={uploadInfo.tags} onChange={(e) => setUploadInfo({...uploadInfo, tags: e.target.value})} />
              <small>Add tags to find this track later</small>
            </div>
            <div className="modal-buttons">
              <button className="btn-primary" onClick={saveTrack} disabled={!uploadInfo.name || uploading}>{uploading ? 'Uploading...' : 'Save Track'}</button>
              <button className="btn-secondary" onClick={() => setShowUploadModal(false)} disabled={uploading}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Share modal */}
      {showShareModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Send Tracks <i className="bi bi-envelope"></i></h2>
            <div className="form-group">
              <label>Your Name</label>
              <input type="text" placeholder="Stage name" value={shareInfo.performerName} onChange={(e) => setShareInfo({...shareInfo, performerName: e.target.value})} />
            </div>
            <div className="form-group">
              <label>Show Name</label>
              <input type="text" placeholder=" Drag Brunch, Viewing Party, etc." value={shareInfo.showName} onChange={(e) => setShareInfo({...shareInfo, showName: e.target.value})} />
            </div>
            <p className="preview-text">
              Subject: <strong>{shareInfo.performerName || 'Your Name'}'s {selectedTracks.length === 1 ? 'song' : 'songs'} for {shareInfo.showName || 'Show Name'}</strong>
            </p>
            <div className="modal-buttons">
              <button className="btn-primary" onClick={sendTracks} disabled={!shareInfo.performerName || !shareInfo.showName}>Send</button>
              <button className="btn-secondary" onClick={() => setShowShareModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}